import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { usePermissions } from '../../hooks/usePermissions';
import { useEscolaComunicados } from '../../api/hooks/useEscolaComunicados';
import { useCrud } from '../../api/hooks/useCrud';
import { ComunicadosCenterCore } from '../../components/comunicados/ComunicadosCenterCore';
import type {
  ComunicadosConfig,
  ComunicadoItem,
  ComunicadoWizardData,
} from '../../components/comunicados/ComunicadosConfig';

interface Turma {
  id: string;
  nome: string;
  ativo: boolean;
}

export function ComunicadosEscolaPage() {
  const location = useLocation();
  const { escolaId, user } = useAuthStore();
  const { canManage } = usePermissions();
  const { data: comunicados = [], isLoading, create, update, deactivate } = useEscolaComunicados();
  const { data: turmas = [] } = useCrud<Turma>('turmas');

  // Captura draft se veio do Banco de Mídias via location.state
  const initialDraft = useMemo(() => {
    const fromMidia = (location.state as any)?.fromMidia;
    if (!fromMidia) return null;
    return {
      titulo: fromMidia.titulo || '',
      categoria: fromMidia.categoria || 'OPERACIONAL',
      tipo: 'AVISO',
      destinatario: 'TODOS',
      configPreview: {
        midiaUrl: fromMidia.arquivoUrl,
        midiaTitulo: fromMidia.titulo,
        midiaId: fromMidia.midiaId,
      },
    };
  }, [location.state]);

  const config: ComunicadosConfig = useMemo(
    () => ({
      scope: 'ESCOLA',
      scopeId: escolaId || '',
      branding: {
        tenantName: (user as any)?.escolaNome || 'Escola Municipal',
        tenantBadge: 'Direção & Coordenação Escolar',
        logoUrl: (user as any)?.escolaLogoUrl,
        primaryColor: '#2563eb',
        secondaryColor: '#1d4ed8',
      },
      labels: {
        heroTitle: 'Central de Comunicados Escolar',
        heroSubtitle:
          'Envie recados, avisos de reuniões, boletins e folders com confirmação de leitura pelas famílias.',
        searchPlaceholder: 'Buscar comunicados da escola...',
        emptyTitle: 'Nenhum comunicado escolar criado ainda',
        emptySubtitle:
          'Inicie o envio de avisos e informativos para os pais e responsáveis dos alunos da escola.',
      },
      features: {
        multiSchoolSelect: false,
        turmaSelect: true,
        templates: true,
        metricsCards: true,
        calendarView: true,
        whatsappPreview: true,
        fixarComunicado: true,
        leituraAudit: true,
        canManage: canManage('comunicados'),
      },
      turmas: turmas.map((t: Turma) => ({ id: t.id, nome: t.nome })),
    }),
    [escolaId, user, turmas],
  );

  return (
    <ComunicadosCenterCore
      config={config}
      comunicados={comunicados as unknown as ComunicadoItem[]}
      isLoading={isLoading}
      onCreate={(data: ComunicadoWizardData) => create.mutateAsync(data as any)}
      onUpdate={(id: string, data: Partial<ComunicadoWizardData>) =>
        update.mutateAsync({ id, ...data } as any)
      }
      onRemove={(id: string) => deactivate.mutateAsync(id)}
      onSendDraft={(c: ComunicadoItem) =>
        update.mutateAsync({ id: c.id, status: 'ENVIADO' } as any)
      }
      initialDraft={initialDraft}
    />
  );
}
